import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useBranch } from '../contexts/BranchContext'
import { expenseService } from '../services/firebase'
import { DollarSign, Plus, Calendar, TrendingUp, PieChart, Download, Search, Edit, Trash2, Receipt, Tag, Filter } from 'lucide-react'
import LoadingSpinner from '../components/LoadingSpinner'
import toast from 'react-hot-toast'
import { cn } from '../utils/cn'

export default function ExpensesPage() {
  const { user } = useAuth()
  const { currentBranch } = useBranch()
  const [expenses, setExpenses] = useState([])
  const [filteredExpenses, setFilteredExpenses] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [showAddModal, setShowAddModal] = useState(false)
  const [selectedPeriod, setSelectedPeriod] = useState('month')
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  })
  const [newExpense, setNewExpense] = useState({
    description: '',
    amount: 0,
    category: '',
    date: new Date().toISOString().split('T')[0],
    receipt: null,
    notes: '',
    paymentMethod: ''
  })
  const [dateFilter, setDateFilter] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('all')
  const [amountFilter, setAmountFilter] = useState('all')

  const categories = [
    'Supplies', 'Utilities', 'Equipment', 'Marketing', 'Staff', 'Maintenance', 
    'Insurance', 'Rent', 'Professional Services', 'Other'
  ]

  useEffect(() => {
    loadExpenses()
  }, [currentBranch, dateRange])

  useEffect(() => {
    filterExpenses()
  }, [expenses, searchTerm, selectedCategory, dateFilter, categoryFilter, amountFilter])

  const loadExpenses = async () => {
    if (!currentBranch) return
    
    try {
      setLoading(true)
      const startDate = new Date(dateRange.start)
      const endDate = new Date(dateRange.end)
      const expenseData = await expenseService.getExpensesByBranch(currentBranch.id, startDate, endDate)
      setExpenses(expenseData)
    } catch (error) {
      console.error('Error loading expenses:', error)
      toast.error('Failed to load expenses')
    } finally {
      setLoading(false)
    }
  }

  const filterExpenses = () => {
    let filtered = expenses

    if (searchTerm) {
      filtered = filtered.filter(expense => 
        expense.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        expense.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
        expense.notes?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(expense => expense.category === selectedCategory)
    }

    if (dateFilter) {
      const filterDate = new Date(dateFilter)
      filtered = filtered.filter(expense => new Date(expense.date) >= filterDate)
    }

    if (categoryFilter !== 'all') {
      filtered = filtered.filter(expense => expense.category === categoryFilter)
    }

    if (amountFilter !== 'all') {
      const amount = parseFloat(newExpense.amount)
      if (amountFilter === 'low' && amount < 100) {
        filtered = filtered.filter(expense => expense.amount < 100)
      } else if (amountFilter === 'medium' && amount >= 100 && amount < 500) {
        filtered = filtered.filter(expense => expense.amount >= 100 && expense.amount < 500)
      } else if (amountFilter === 'high' && amount >= 500) {
        filtered = filtered.filter(expense => expense.amount >= 500)
      }
    }

    setFilteredExpenses(filtered)
  }

  const handleAddExpense = async (e) => {
    e.preventDefault()
    
    try {
      const expenseData = {
        ...newExpense,
        branchId: currentBranch.id,
        userId: user.uid,
        amount: parseFloat(newExpense.amount)
      }
      
      await expenseService.addExpense(expenseData)
      toast.success('Expense added successfully')
      setShowAddModal(false)
      setNewExpense({
        description: '',
        amount: 0,
        category: '',
        date: new Date().toISOString().split('T')[0],
        receipt: null,
        notes: '',
        paymentMethod: ''
      })
      loadExpenses()
    } catch (error) {
      console.error('Error adding expense:', error)
      toast.error('Failed to add expense')
    }
  }

  const handleDeleteExpense = async (expenseId) => {
    if (!window.confirm('Are you sure you want to delete this expense?')) return
    
    try {
      await expenseService.deleteExpense(expenseId)
      toast.success('Expense deleted successfully')
      loadExpenses()
    } catch (error) {
      console.error('Error deleting expense:', error)
      toast.error('Failed to delete expense')
    }
  }

  const getTotalExpenses = () => {
    return filteredExpenses.reduce((total, expense) => total + expense.amount, 0)
  }

  const getMonthlyExpenses = () => {
    const startDate = new Date(new Date().getFullYear(), new Date().getMonth(), 1)
    const endDate = new Date()
    const monthlyExpenses = expenses.filter(expense => 
      new Date(expense.date) >= startDate && new Date(expense.date) <= endDate
    )
    return monthlyExpenses.reduce((total, expense) => total + expense.amount, 0)
  }

  const getUniqueCategories = () => {
    const unique = new Set()
    filteredExpenses.forEach(expense => {
      unique.add(expense.category)
    })
    return Array.from(unique)
  }

  const getAverageExpense = () => {
    const totalExpenses = getTotalExpenses()
    const totalRecords = filteredExpenses.length
    return totalRecords > 0 ? (totalExpenses / totalRecords).toFixed(2) : '0.00'
  }

  const setPeriod = (period) => {
    const now = new Date()
    let start, end
    
    switch (period) {
      case 'week':
        start = new Date(now.setDate(now.getDate() - 7))
        end = new Date()
        break
      case 'month':
        start = new Date(now.getFullYear(), now.getMonth(), 1)
        end = new Date()
        break
      case 'quarter':
        const quarter = Math.floor(now.getMonth() / 3)
        start = new Date(now.getFullYear(), quarter * 3, 1)
        end = new Date()
        break
      case 'year':
        start = new Date(now.getFullYear(), 0, 1)
        end = new Date()
        break
    }
    
    setDateRange({
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0]
    })
    setSelectedPeriod(period)
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background pb-8">
      <div className="max-w-7xl mx-auto px-4 py-8 space-y-8">
        
        {/* Header */}
        <div className="text-center space-y-4">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-primary/10 text-primary mb-4">
            <Receipt className="h-10 w-10" />
          </div>
          <h1 className="text-3xl font-semibold tracking-tight text-foreground">
            Expense Management
          </h1>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Track and manage all business expenses and costs
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="rounded-xl border border-border/80 bg-card shadow-sm p-6 hover:shadow-xl transition-all duration-300">
            <div className="flex items-center">
              <div className="w-12 h-12 rounded-xl bg-rose-500/10 flex items-center justify-center mr-4 text-rose-600 dark:text-rose-400">
                <Receipt className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Total Expenses</p>
                <p className="text-2xl font-bold text-gray-900">R{getTotalExpenses().toFixed(2)}</p>
              </div>
            </div>
          </div>
          
          <div className="rounded-xl border border-border/80 bg-card shadow-sm p-6 hover:shadow-xl transition-all duration-300">
            <div className="flex items-center">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mr-4 text-primary">
                <Calendar className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">This Month</p>
                <p className="text-2xl font-bold text-gray-900">R{getMonthlyExpenses().toFixed(2)}</p>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-border/80 bg-card shadow-sm p-6 hover:shadow-xl transition-all duration-300">
            <div className="flex items-center">
              <div className="w-12 h-12 rounded-xl bg-violet-500/10 flex items-center justify-center mr-4 text-violet-700 dark:text-violet-400">
                <Tag className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Categories</p>
                <p className="text-2xl font-bold text-gray-900">{getUniqueCategories().length}</p>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-border/80 bg-card shadow-sm p-6 hover:shadow-xl transition-all duration-300">
            <div className="flex items-center">
              <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center mr-4 text-amber-800 dark:text-amber-400">
                <TrendingUp className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Avg. Expense</p>
                <p className="text-2xl font-bold text-gray-900">R{getAverageExpense()}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Action Button */}
        <div className="flex justify-center">
          <button
            onClick={() => setShowAddModal(true)}
            className="rounded-xl bg-primary px-8 py-4 font-semibold text-primary-foreground shadow-sm transition hover:bg-primary/90"
          >
            <Plus className="h-5 w-5 inline mr-2 group-hover:rotate-90 transition-transform duration-300" />
            Add New Expense
          </button>
        </div>

        {/* Filters */}
        <div className="rounded-xl border border-border/80 bg-card shadow-sm p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search expenses..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-white/50 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
              />
            </div>
            <div className="relative">
              <Calendar className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="date"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-white/50 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
              />
            </div>
            <div className="relative">
              <Filter className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-white/50 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 appearance-none"
              >
                <option value="all">All Categories</option>
                {getUniqueCategories().map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>
            <div className="relative">
              <DollarSign className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <select
                value={amountFilter}
                onChange={(e) => setAmountFilter(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-white/50 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 appearance-none"
              >
                <option value="all">All Amounts</option>
                <option value="low">Under R100</option>
                <option value="medium">R100 - R500</option>
                <option value="high">Over R500</option>
              </select>
            </div>
          </div>
        </div>

        {/* Expenses Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredExpenses.map((expense, index) => (
            <div 
              key={expense.id}
              className="group relative rounded-xl border border-border/80 bg-card shadow-sm hover:shadow-xl transition-all duration-300 hover:scale-105 overflow-hidden"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {/* Amount indicator */}
              <div className={cn(
                'absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-medium border-2 border-white shadow-md',
                expense.amount < 100 ? 'bg-green-100 text-green-800' : 
                expense.amount < 500 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'
              )}>
                {expense.amount < 100 ? 'Low' : expense.amount < 500 ? 'Medium' : 'High'}
              </div>
              
              <div className="p-6">
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center">
                    <div className="w-12 h-12 rounded-xl bg-rose-500/10 flex items-center justify-center mr-3 text-rose-600 dark:text-rose-400">
                      <Receipt className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">{expense.description}</h3>
                      <p className="text-sm text-gray-600">{expense.category}</p>
                    </div>
                  </div>
                </div>

                {/* Expense Details */}
                <div className="space-y-3 mb-6">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Date:</span>
                    <span className="font-medium text-gray-900">{new Date(expense.date).toLocaleDateString()}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Amount:</span>
                    <span className="font-medium text-gray-900">R{expense.amount?.toFixed(2) || '0.00'}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Payment Method:</span>
                    <span className="font-medium text-gray-900">{expense.paymentMethod || 'N/A'}</span>
                  </div>
                </div>

                {/* Financial Info */}
                <div className="grid grid-cols-2 gap-3 mb-6">
                  <div className="bg-rose-500/5 rounded-xl p-3 text-center">
                    <div className="text-lg font-bold text-red-600">R{expense.amount?.toFixed(2) || '0.00'}</div>
                    <div className="text-xs text-gray-600">Amount</div>
                  </div>
                  <div className="bg-primary/5 rounded-xl p-3 text-center">
                    <div className="text-lg font-bold text-blue-600">{expense.category}</div>
                    <div className="text-xs text-gray-600">Category</div>
                  </div>
                </div>

                {/* Notes */}
                {expense.notes && (
                  <div className="mb-6">
                    <div className="bg-muted/50 rounded-xl p-3">
                      <p className="text-sm text-gray-700">{expense.notes}</p>
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex items-center justify-between">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEditExpense(expense)}
                      className="p-2 text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50 rounded-xl transition-all duration-200"
                      title="Edit Expense"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteExpense(expense.id)}
                      className="p-2 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-xl transition-all duration-200"
                      title="Delete Expense"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  
                  <span className={cn(
                    "inline-flex items-center px-3 py-1 rounded-full text-xs font-medium",
                    expense.amount < 100 ? "bg-green-100 text-green-800" : 
                    expense.amount < 500 ? "bg-yellow-100 text-yellow-800" : "bg-red-100 text-red-800"
                  )}>
                    {expense.amount < 100 ? 'Low Cost' : 
                     expense.amount < 500 ? 'Medium Cost' : 'High Cost'}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {filteredExpenses.length === 0 && (
          <div className="text-center py-12">
            <div className="w-24 h-24 bg-muted rounded-3xl flex items-center justify-center mx-auto mb-6">
              <Receipt className="h-12 w-12 text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No expenses found</h3>
            <p className="text-gray-600 mb-6">
              {searchTerm || dateFilter || categoryFilter !== 'all' || amountFilter !== 'all'
                ? 'Try adjusting your search or filter criteria.'
                : 'Get started by adding your first expense record.'
              }
            </p>
            {!searchTerm && !dateFilter && categoryFilter === 'all' && amountFilter === 'all' && (
              <button
                onClick={() => setShowAddModal(true)}
                className="rounded-xl bg-primary px-6 py-3 font-semibold text-primary-foreground shadow-sm transition hover:bg-primary/90"
              >
                <Plus className="h-5 w-5 inline mr-2" />
                Add First Expense
              </button>
            )}
          </div>
        )}
      </div>

      {/* Add/Edit Expense Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center mb-6">
                <div className="w-10 h-10 rounded-lg bg-rose-500/10 flex items-center justify-center mr-3 text-rose-600 dark:text-rose-400">
                  <Receipt className="h-5 w-5" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Add New Expense</h3>
              </div>
              
              <form onSubmit={handleAddExpense} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Description *</label>
                  <input
                    type="text"
                    required
                    value={newExpense.description}
                    onChange={(e) => setNewExpense({...newExpense, description: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                  />
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Amount (R) *</label>
                    <input
                      type="number"
                      required
                      min="0"
                      step="0.01"
                      value={newExpense.amount}
                      onChange={(e) => setNewExpense({...newExpense, amount: parseFloat(e.target.value)})}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Date *</label>
                    <input
                      type="date"
                      required
                      value={newExpense.date}
                      onChange={(e) => setNewExpense({...newExpense, date: e.target.value})}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Category *</label>
                    <select
                      required
                      value={newExpense.category}
                      onChange={(e) => setNewExpense({...newExpense, category: e.target.value})}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                    >
                      <option value="">Select Category</option>
                      <option value="Utilities">Utilities</option>
                      <option value="Supplies">Supplies</option>
                      <option value="Equipment">Equipment</option>
                      <option value="Maintenance">Maintenance</option>
                      <option value="Marketing">Marketing</option>
                      <option value="Insurance">Insurance</option>
                      <option value="Rent">Rent</option>
                      <option value="Salaries">Salaries</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Payment Method</label>
                    <select
                      value={newExpense.paymentMethod}
                      onChange={(e) => setNewExpense({...newExpense, paymentMethod: e.target.value})}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                    >
                      <option value="">Select Method</option>
                      <option value="Cash">Cash</option>
                      <option value="Card">Card</option>
                      <option value="Bank Transfer">Bank Transfer</option>
                      <option value="Check">Check</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Notes</label>
                  <textarea
                    value={newExpense.notes}
                    onChange={(e) => setNewExpense({...newExpense, notes: e.target.value})}
                    rows="3"
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 resize-none"
                    placeholder="Additional notes about this expense..."
                  />
                </div>
                
                <div className="flex space-x-3 pt-4">
                  <button
                    type="submit"
                    className="flex-1 rounded-xl bg-primary py-3 px-4 font-semibold text-primary-foreground shadow-sm transition hover:bg-primary/90"
                  >
                    Add Expense
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="flex-1 bg-gray-100 text-gray-700 py-3 px-4 rounded-2xl font-semibold hover:bg-gray-200 transition-all duration-300"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
} 